import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, ButtonGroup, Card, Col, Row, Spinner } from 'react-bootstrap'
import { Bar, Doughnut } from 'react-chartjs-2'
import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip } from 'chart.js'
import { fetchObjednavky } from '../services/api/objednavky'
import { apiClient } from '../services/api/client'
import { useAutoRefresh } from '../hooks/useAutoRefresh'

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend)

const SLOTS_PER_DAY = 20

const STATUS_WIDGETS = [
  { key: 'prijato', label: 'Přijato', subtitle: 'nově přijaté objednávky', icon: 'bi-clipboard-check', variant: 'secondary' },
  { key: 'dilna', label: 'Dílna', subtitle: 'rozpracované objednávky v dílně', icon: 'bi-wrench', variant: 'secondary' },
  { key: 'provedeno', label: 'Provedeno', subtitle: 'objednávky připravené k vyúčtování', icon: 'bi-hourglass-split', variant: 'secondary' },
  { key: 'vyrizeno', label: 'Vyřízeno', subtitle: 'vyřízené objednávky', icon: 'bi-check2-circle', variant: 'secondary' }
]

const normalizeText = (value) =>
  String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '')
    .trim()

const toNum = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

const toDate = (v) => {
  if (!v) return null
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}

const dateOnlyDiffDays = (fromValue, toValue) => {
  const from = toDate(fromValue)
  const to = toDate(toValue)
  if (!from || !to) return null
  const fromDay = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  const toDay = new Date(to.getFullYear(), to.getMonth(), to.getDate())
  const diff = Math.floor((toDay.getTime() - fromDay.getTime()) / 86400000)
  return diff >= 0 ? diff : null
}

const derivePreviousYearPrefix = (prefix) => {
  const p = String(prefix || '').trim().toUpperCase()
  const m = p.match(/^([A-Z])(\d{2})(.+)$/)
  if (!m) return ''
  const marker = m[1]
  const yy = Number(m[2])
  const suffix = m[3]
  if (!Number.isFinite(yy)) return ''
  const prevYY = String((yy + 99) % 100).padStart(2, '0')
  return `${marker}${prevYY}${suffix}`
}

const getNiceStep = (maxValue, segments = 8) => {
  const rawStep = Math.max(maxValue, 1) / segments
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)))
  const residual = rawStep / magnitude
  let niceResidual = 1
  if (residual > 1 && residual <= 2) niceResidual = 2
  else if (residual > 2 && residual <= 5) niceResidual = 5
  else if (residual > 5) niceResidual = 10
  return niceResidual * magnitude
}

const isCzechHoliday = (date) => {
  const year = date.getFullYear()
  const fixed = new Set([
    `${year}-01-01`,
    `${year}-05-01`,
    `${year}-05-08`,
    `${year}-07-05`,
    `${year}-07-06`,
    `${year}-09-28`,
    `${year}-10-28`,
    `${year}-11-17`,
    `${year}-12-24`,
    `${year}-12-25`,
    `${year}-12-26`
  ])
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const g = Math.floor((8 * b + 13) / 25)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1
  const day = ((h + l - 7 * m + 114) % 31) + 1
  const easterSunday = new Date(year, month, day)
  const goodFriday = new Date(easterSunday)
  goodFriday.setDate(goodFriday.getDate() - 2)
  const easterMonday = new Date(easterSunday)
  easterMonday.setDate(easterMonday.getDate() + 1)
  fixed.add(goodFriday.toISOString().slice(0, 10))
  fixed.add(easterMonday.toISOString().slice(0, 10))
  return fixed.has(date.toISOString().slice(0, 10))
}

const startOfWeek = (date) => {
  const d = new Date(date)
  const day = (d.getDay() + 6) % 7
  d.setDate(d.getDate() - day)
  d.setHours(0, 0, 0, 0)
  return d
}

const getPeriodBounds = (mode, anchor) => {
  const a = new Date(anchor)
  a.setHours(0, 0, 0, 0)
  if (mode === 'day') {
    const start = new Date(a)
    const end = new Date(a)
    end.setDate(end.getDate() + 1)
    return { start, end }
  }
  if (mode === 'week') {
    const start = startOfWeek(a)
    const end = new Date(start)
    end.setDate(end.getDate() + 7)
    return { start, end }
  }
  const start = new Date(a.getFullYear(), a.getMonth(), 1)
  const end = new Date(a.getFullYear(), a.getMonth() + 1, 1)
  return { start, end }
}

const formatCurrency = (value) =>
  new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 0 }).format(toNum(value))

const formatMonthYear = (date) =>
  `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getFullYear()).slice(-2)}`

const DashboardAdmin = () => {
  const [strediskoConfig, setStrediskoConfig] = useState(null)
  const [orders, setOrders] = useState([])
  const [reservations, setReservations] = useState([])
  const [requests, setRequests] = useState([])
  const [storages, setStorages] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [utilMode, setUtilMode] = useState('day')
  const [anchorDate, setAnchorDate] = useState(() => new Date())

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true)
      setError('')

      const strediskaData = await apiClient.get('/strediska')
      const list = Array.isArray(strediskaData) ? strediskaData : []
      const primaryStredisko = list[0] || null
      setStrediskoConfig(primaryStredisko)

      const currentPrefix = String(primaryStredisko?.prefix_mrp || '').trim().toUpperCase()
      const prevPrefix = derivePreviousYearPrefix(currentPrefix)
      const prefixesWithPrevYear = Array.from(new Set([currentPrefix, prevPrefix].filter(Boolean)))

      const [ordersData, reservationsData, requestsData, storagesData] = await Promise.all([
        prefixesWithPrevYear.length ? fetchObjednavky({ prefixes: prefixesWithPrevYear, limit: 3000 }) : Promise.resolve([]),
        apiClient.get('/rezervace'),
        apiClient.get('/poptavky'),
        apiClient.get('/uschovy')
      ])

      setOrders(Array.isArray(ordersData) ? ordersData : [])
      setReservations(Array.isArray(reservationsData) ? reservationsData : [])
      setRequests(Array.isArray(requestsData) ? requestsData : [])
      setStorages(Array.isArray(storagesData) ? storagesData : [])
    } catch (e) {
      setError(e.message || 'Nepodařilo se načíst dashboard.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadDashboard()
  }, [loadDashboard])

  useAutoRefresh(loadDashboard)

  const selectedPrefixCurrent = useMemo(
    () => String(strediskoConfig?.prefix_mrp || '').trim().toUpperCase(),
    [strediskoConfig]
  )

  const selectedPrefixesForChart = useMemo(() => {
    const prev = derivePreviousYearPrefix(selectedPrefixCurrent)
    return Array.from(new Set([selectedPrefixCurrent, prev].filter(Boolean)))
  }, [selectedPrefixCurrent])

  const filteredOrdersCurrent = useMemo(() => {
    if (!selectedPrefixCurrent) return []
    return (orders || []).filter((o) => {
      const cislo = String(o.CISLO || '').trim().toUpperCase()
      return cislo.startsWith(selectedPrefixCurrent)
    })
  }, [orders, selectedPrefixCurrent])

  const filteredOrdersForChart = useMemo(() => {
    if (!selectedPrefixesForChart.length) return []
    return (orders || []).filter((o) => {
      const cislo = String(o.CISLO || '').trim().toUpperCase()
      return selectedPrefixesForChart.some((p) => cislo.startsWith(p))
    })
  }, [orders, selectedPrefixesForChart])

  const requestsStats = useMemo(() => {
    const base = { total: 0, open: 0, done: 0, qty: 0, amount: 0 }
    ;(requests || []).forEach((r) => {
      const stav = Number(r.stav || 0)
      base.total += 1
      if (stav === 1) base.done += 1
      else base.open += 1
      base.qty += toNum(r.pocet)
      base.amount += toNum(r.cena)
    })
    return base
  }, [requests])

  const storagesStats = useMemo(() => {
    const base = {
      total: 0,
      active: 0,
      released: 0,
      pneus: 0,
      leto: 0,
      zima: 0,
      celorocni: 0,
      releasedDaysTotal: 0,
      releasedDaysCount: 0,
      avgReleasedDays: null
    }
    ;(storages || []).forEach((s) => {
      const stav = Number(s.stav ?? 1)
      const sezona = Number(s.sezona || 0)
      const pocetPneu = toNum(s.pneu_pocet)
      base.total += 1
      if (stav === 1) {
        base.active += 1
        base.pneus += pocetPneu
        if (sezona === 1) base.leto += 1
        else if (sezona === 2) base.zima += 1
        else if (sezona === 3) base.celorocni += 1
      } else {
        base.released += 1
        const days = dateOnlyDiffDays(s.datum_prijmu, s.datum_vydeje)
        if (Number.isFinite(days)) {
          base.releasedDaysTotal += days
          base.releasedDaysCount += 1
        }
      }
    })
    if (base.releasedDaysCount > 0) {
      base.avgReleasedDays = base.releasedDaysTotal / base.releasedDaysCount
    }
    return base
  }, [storages])

  const widgetStats = useMemo(() => {
    const base = {
      prijato: { count: 0, amount: 0 },
      dilna: { count: 0, amount: 0 },
      provedeno: { count: 0, amount: 0 },
      vyrizeno: { count: 0, amount: 0 }
    }
    filteredOrdersCurrent.forEach((o) => {
      const vybavene = Number(o.VYBAVENE)
      const stav = normalizeText(o.STAV)
      const amount = toNum(o.CELKOM)
      if (vybavene === 0) {
        base.vyrizeno.count += 1
        base.vyrizeno.amount += amount
      } else if (vybavene === 2 && stav === 'dilna') {
        base.dilna.count += 1
        base.dilna.amount += amount
      } else if (vybavene === 2 && stav === 'provedeno') {
        base.provedeno.count += 1
        base.provedeno.amount += amount
      } else if (vybavene === 2) {
        base.prijato.count += 1
        base.prijato.amount += amount
      }
    })
    return base
  }, [filteredOrdersCurrent])

  const monthlyDoneSeries = useMemo(() => {
    const now = new Date()
    const months = Array.from({ length: 12 }, (_, idx) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (11 - idx), 1)
      return {
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        label: formatMonthYear(d),
        total: 0
      }
    })
    const map = new Map(months.map((m) => [m.key, m]))
    filteredOrdersForChart.forEach((o) => {
      if (Number(o.VYBAVENE) !== 0) return
      const d = new Date(o.DATUM)
      if (Number.isNaN(d.getTime())) return
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      const target = map.get(key)
      if (target) target.total += toNum(o.CELKOM)
    })
    return months
  }, [filteredOrdersForChart])

  const monthMax = useMemo(() => {
    const max = Math.max(...monthlyDoneSeries.map((m) => m.total), 0)
    return max > 0 ? max : 1
  }, [monthlyDoneSeries])

  const chartMax = useMemo(() => {
    const segments = 8
    const step = getNiceStep(monthMax, segments)
    return Math.max(Math.ceil(monthMax / step) * step, step * segments)
  }, [monthMax])

  const monthlyChartData = useMemo(() => ({
    labels: monthlyDoneSeries.map((m) => m.label),
    datasets: [
      {
        label: 'objem vyřízených objednávek',
        data: monthlyDoneSeries.map((m) => toNum(m.total)),
        backgroundColor: 'rgba(13, 110, 253, 0.18)',
        borderColor: 'rgba(13, 110, 253, 0.32)',
        borderWidth: 1,
        borderRadius: 2,
        maxBarThickness: 34
      }
    ]
  }), [monthlyDoneSeries])

  const monthlyChartOptions = useMemo(() => {
    const stepSize = chartMax / 8
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 10,
            boxHeight: 10,
            color: 'rgba(33, 37, 41, 0.7)',
            font: { size: 12 }
          }
        },
        tooltip: {
          callbacks: {
            label: (ctx) => ` ${formatCurrency(ctx.parsed.y)}`
          }
        }
      },
      scales: {
        x: {
          grid: {
            color: 'rgba(33, 37, 41, 0.08)'
          },
          ticks: {
            color: 'rgba(33, 37, 41, 0.6)',
            font: { size: 11 }
          }
        },
        y: {
          beginAtZero: true,
          max: chartMax,
          ticks: {
            stepSize,
            color: 'rgba(33, 37, 41, 0.55)',
            font: { size: 11 },
            callback: (value) => Number(value).toLocaleString('cs-CZ')
          },
          grid: {
            color: 'rgba(33, 37, 41, 0.08)'
          }
        }
      }
    }
  }, [chartMax])

  const period = useMemo(() => getPeriodBounds(utilMode, anchorDate), [utilMode, anchorDate])

  const utilization = useMemo(() => {
    const { start, end } = period
    let workingDays = 0
    for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
      const day = d.getDay()
      if (day === 0 || day === 6) continue
      if (isCzechHoliday(d)) continue
      workingDays += 1
    }
    const totalStani = toNum(strediskoConfig?.stani)
    const totalSlots = workingDays * totalStani * SLOTS_PER_DAY

    const occupied = (reservations || []).filter((r) => {
      const startDate = new Date(r.zacatek)
      return startDate >= start && startDate < end
    }).length

    const percent = totalSlots > 0 ? Math.min((occupied / totalSlots) * 100, 100) : 0
    return { occupied, totalSlots, percent }
  }, [period, strediskoConfig, reservations])

  const utilizationChartData = useMemo(() => {
    const value = Math.max(0, Math.min(utilization.percent, 100))
    return {
      labels: ['Obsazeno', 'Volno'],
      datasets: [
        {
          data: [value, 100 - value],
          backgroundColor: ['rgba(13, 110, 253, 0.32)', 'rgba(13, 110, 253, 0.08)'],
          borderColor: ['rgba(13, 110, 253, 0.42)', 'rgba(13, 110, 253, 0.12)'],
          borderWidth: 1,
          hoverOffset: 0
        }
      ]
    }
  }, [utilization.percent])

  const utilizationChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: '76%',
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false }
    }
  }), [])

  const periodLabel = useMemo(() => {
    const { start, end } = period
    if (utilMode === 'day') {
      return new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' }).format(start)
    }
    if (utilMode === 'week') {
      const endMinus = new Date(end)
      endMinus.setDate(endMinus.getDate() - 1)
      const fmt = new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'numeric' })
      return `${fmt.format(start)} - ${fmt.format(endMinus)}`
    }
    return new Intl.DateTimeFormat('cs-CZ', { month: 'long', year: 'numeric' }).format(start)
  }, [period, utilMode])

  const shiftPeriod = (direction) => {
    setAnchorDate((prev) => {
      const d = new Date(prev)
      if (utilMode === 'day') d.setDate(d.getDate() + direction)
      if (utilMode === 'week') d.setDate(d.getDate() + 7 * direction)
      if (utilMode === 'month') d.setMonth(d.getMonth() + direction)
      return d
    })
  }

  return (
    <>
      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner />
        </div>
      ) : (
        <>
          <Row className="g-3 mt-2">
            {STATUS_WIDGETS.map((w) => (
              <Col xs={12} sm={6} xl={3} key={w.key}>
                <Card className="border-0 rounded-3 h-100">
                  <Card.Body className="p-3 p-xl-4">
                    <div className="d-flex flex-column flex-sm-row gap-3 align-items-start align-items-sm-center">
                      <div className={`rounded-circle d-flex align-items-center justify-content-center bg-${w.variant}-subtle text-${w.variant}`} style={{ width: 56, height: 56 }}>
                        <i className={`bi ${w.icon} fs-4`}></i>
                      </div>
                      <div className="flex-grow-1">
                        <div className="fs-5 lh-1">{w.label}</div>
                        <div className="small text-muted mt-1 text-secondary">{w.subtitle}</div>
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-end mt-3 text-secondary gap-2">
                          <div className="display-6 fw-bold lh-1">{widgetStats[w.key].count}</div>
                          <div className="fs-4 fw-semibold lh-1">{formatCurrency(widgetStats[w.key].amount)}</div>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <Row className="g-3 mt-1">
            <Col xxl={8} lg={7} md={6} sm={12}>
                <Card className="border-0 rounded-3 h-100">
                <Card.Header className="bg-white border-bottom-0 p-4">
                  <Card.Title className="mb-0">Objednávky vývoj</Card.Title>
                  <Card.Subtitle className="small text-muted mt-1">měsíční vývoj objemu vyřízených objednávek za posledních 12 měsíců</Card.Subtitle>
                </Card.Header>
                <Card.Body className="border-top p-4">
                  <div style={{ height: 360 }}>
                    <Bar data={monthlyChartData} options={monthlyChartOptions} />
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xxl={4} lg={5} md={6} sm={12}>
              <Card className="h-100 border-0">
                <Card.Header className="bg-white border-bottom-0 p-4">
                  <Card.Title className="mb-0">Rezervace</Card.Title>
                  <Card.Subtitle className="small text-muted mt-1">přehled vytížení střediska</Card.Subtitle>
                </Card.Header>
                <Card.Body className="border-top d-flex flex-column p-4">
                  <div className="d-flex justify-content-center align-items-center gap-2 mb-3">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => {
                        setUtilMode('day')
                        setAnchorDate(new Date())
                      }}
                    >
                      Dnes
                    </Button>
                    <ButtonGroup size="sm">
                      <Button variant={utilMode === 'day' ? 'secondary' : 'outline-secondary'} onClick={() => setUtilMode('day')}>Den</Button>
                      <Button variant={utilMode === 'week' ? 'secondary' : 'outline-secondary'} onClick={() => setUtilMode('week')}>Týden</Button>
                      <Button variant={utilMode === 'month' ? 'secondary' : 'outline-secondary'} onClick={() => setUtilMode('month')}>Měsíc</Button>
                    </ButtonGroup>
                  </div>

                  <div className="d-flex justify-content-center">
                    {/* <span className="badge rounded-pill text-secondary-emphasis bg-secondary-subtle border border-secondary-subtle px-3 py-2"> */}
                    <span className="text-secondary py-2">
                      {periodLabel}
                    </span>
                  </div>

                  <div className="d-flex flex-column flex-sm-row align-items-center justify-content-between gap-3 flex-grow-1">
                    <Button size="sm" variant="outline-secondary" className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 36, height: 36 }} onClick={() => shiftPeriod(-1)}>
                      <i className="bi bi-chevron-left"></i>
                    </Button>

                    <div className="position-relative d-flex justify-content-center align-items-center flex-grow-1 h-100">
                      <div style={{ width: 'min(240px, 62vw)', height: 'min(240px, 62vw)' }}>
                        <Doughnut data={utilizationChartData} options={utilizationChartOptions} />
                      </div>
                      <div className="position-absolute text-center">
                        <div className="fs-2 fw-bold text-secondary">{Math.round(utilization.percent)} %</div>
                        <div className="small text-secondary">
                          {utilization.occupied} / {utilization.totalSlots} slotů
                        </div>
                      </div>
                    </div>

                    <Button size="sm" variant="outline-secondary" className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 36, height: 36 }} onClick={() => shiftPeriod(1)}>
                      <i className="bi bi-chevron-right"></i>
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col md={6}>
              <Card className="border-0 rounded-3 h-100">
                <Card.Header className="bg-white border-bottom-0 p-4">
                  <Card.Title className="mb-0">Poptávky</Card.Title>
                  <Card.Subtitle className="small text-muted mt-1">přehled objednávaného zboží u dodavatelů</Card.Subtitle>
                </Card.Header>
                <Card.Body className="border-top p-4">
                  <div className="d-flex justify-content-between align-items-end mb-3">
                    <div className="d-flex gap-3 align-items-center">
                      <div className="rounded-circle d-flex align-items-center justify-content-center bg-secondary-subtle text-secondary" style={{ width: 56, height: 56 }}>
                        <i className="bi bi-box2 fs-4"></i>
                      </div>
                      <div>
                        <div className="small text-muted">Celkem poptávek</div>
                        <div className="display-6 fw-bold lh-1 text-secondary">{requestsStats.total}</div>
                      </div>
                    </div>
                    <div className="text-end">
                      <div className="small text-muted">Objednané množství</div>
                      <div className="fs-4 fw-semibold lh-1 text-secondary">{requestsStats.qty.toLocaleString('cs-CZ', { maximumFractionDigits: 2 })}</div>
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <span className="badge text-secondary-emphasis bg-secondary-subtle border border-secondary-subtle px-3 py-2">
                      <i className="bi bi-clipboard-check me-1"></i>
                      Nevyřízeno: {requestsStats.open}
                    </span>
                    <span className="badge text-success-emphasis bg-success-subtle border border-success-subtle px-3 py-2">
                      <i className="bi bi-check2-circle me-1"></i>
                      Vyřízeno: {requestsStats.done}
                    </span>
                  </div>
                  <div className="small text-muted">Celková hodnota</div>
                  <div className="fs-4 fw-semibold text-secondary">{formatCurrency(requestsStats.amount)}</div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="border-0 rounded-3 h-100">
                <Card.Header className="bg-white border-bottom-0 p-4">
                  <Card.Title className="mb-0">Úschovy</Card.Title>
                  <Card.Subtitle className="small text-muted mt-1">správa sezónních úschov pneu dle stavu a sezóny</Card.Subtitle>
                </Card.Header>
                <Card.Body className="border-top p-4">
                  <div className="d-flex justify-content-between align-items-end mb-3">
                    <div className="d-flex gap-3 align-items-center">
                      <div className="rounded-circle d-flex align-items-center justify-content-center bg-secondary-subtle text-secondary" style={{ width: 56, height: 56 }}>
                        <i className="bi bi-safe fs-4"></i>
                      </div>
                      <div>
                        <div className="small text-muted">Aktivní úschovy</div>
                        <div className="display-6 fw-bold lh-1 text-secondary">{storagesStats.active}</div>
                      </div>
                    </div>
                    <div className="text-end">
                      <div className="small text-muted">Pneu v úschově</div>
                      <div className="fs-4 fw-semibold lh-1 text-secondary">{storagesStats.pneus.toLocaleString('cs-CZ')}</div>
                    </div>
                    <div className="text-end">
                      <div className="small text-muted">Průměrná délka úschovy</div>
                      <div className="fs-4 fw-semibold lh-1 text-secondary">{storagesStats.avgReleasedDays == null
                      ? '—'
                      : `${storagesStats.avgReleasedDays.toLocaleString('cs-CZ', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} dní`}</div>
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <span className="badge text-secondary-emphasis bg-secondary-subtle border border-secondary-subtle px-3 py-2">
                      <i className="bi bi-clipboard-check me-1"></i>
                      Celkem smluv: {storagesStats.total}
                    </span>
                    <span className="badge text-success-emphasis bg-success-subtle border border-success-subtle px-3 py-2">
                      <i className="bi bi-check2-circle me-1"></i>
                      Vydáno: {storagesStats.released}
                    </span>
                  </div>
                  <div className="small text-muted mb-1">Sezóna (aktivní úschovy)</div>
                  <div className="d-flex flex-wrap gap-2">
                    <span className="badge text-warning-emphasis bg-warning-subtle border border-warning-subtle px-3 py-2">
                      <i className="bi bi-sun me-1"></i>
                      Léto: {storagesStats.leto}
                    </span>
                    <span className="badge text-info-emphasis bg-info-subtle border border-info-subtle px-3 py-2">
                      <i className="bi bi-snow me-1"></i>
                      Zima: {storagesStats.zima}
                    </span>
                    <span className="badge text-secondary-emphasis bg-secondary-subtle border border-secondary-subtle px-3 py-2">
                      <i className="bi bi-c-circle me-1"></i>
                      Celoroční: {storagesStats.celorocni}
                    </span>
                  </div>                  
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </>
  )
}

export default DashboardAdmin

